import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { CATEGORIES } from "../data/office";
import Reveal from "./Reveal";

export default function Areas({ onConsult }) {
  const [active, setActive] = useState(CATEGORIES[0].slug);
  const cat = CATEGORIES.find((c) => c.slug === active);

  return (
    <section id="areas" data-testid="areas-section" className="relative bg-navy-900 py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <p className="font-mono-meta text-[11px] uppercase tracking-[0.35em] text-gold-400">
            Áreas de atuação
          </p>
          <h2 className="font-display mt-4 max-w-2xl text-4xl font-medium leading-tight text-cream sm:text-5xl lg:text-6xl">
            Em que podemos <em className="gold-text">ajudar</em> hoje?
          </h2>
        </Reveal>

        <Reveal delay={0.1}>
          <div
            role="tablist"
            aria-label="Seletor de áreas de atuação"
            className="mt-12 flex gap-2 overflow-x-auto pb-3 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            data-testid="areas-tabs"
          >
            {CATEGORIES.map((c) => {
              const Icon = c.icon;
              const isActive = c.slug === active;
              return (
                <button
                  key={c.slug}
                  role="tab"
                  aria-selected={isActive}
                  data-testid={`areas-tab-${c.slug}`}
                  onClick={() => setActive(c.slug)}
                  className={`relative flex shrink-0 items-center gap-2.5 rounded-full px-5 py-3 text-sm font-semibold transition-colors duration-300 ${
                    isActive ? "text-navy-950" : "text-mist hover:text-cream"
                  }`}
                >
                  {isActive && (
                    <motion.span
                      layoutId="tab-pill"
                      className="absolute inset-0 rounded-full bg-gold-500"
                      transition={{ type: "spring", stiffness: 380, damping: 32 }}
                    />
                  )}
                  <Icon className="relative z-10 h-4 w-4" />
                  <span className="relative z-10 whitespace-nowrap">{c.label}</span>
                </button>
              );
            })}
          </div>
          <div className="h-px w-full gold-rule opacity-40" />
        </Reveal>

        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
          >
            <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <p className="max-w-2xl text-base leading-relaxed text-mist sm:text-lg" data-testid="areas-tagline">
                {cat.tagline}
              </p>
              <Link
                to={`/areas/${cat.slug}`}
                data-testid={`areas-more-${cat.slug}`}
                className="inline-flex shrink-0 items-center gap-2 text-sm font-semibold text-gold-400 underline-offset-4 hover:underline"
              >
                Saiba mais sobre {cat.label} <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {cat.solutions.map((s, i) => {
                const Icon = s.icon;
                const featured = i === 0;
                return (
                  <Reveal key={s.title} delay={i * 0.06} className={featured ? "md:col-span-2 lg:col-span-1" : ""}>
                    <article
                      data-testid={`solution-card-${cat.slug}-${i}`}
                      className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-navy-600/60 bg-card p-7 transition-all duration-500 hover:-translate-y-1 hover:border-gold-500/50 hover:shadow-[0_20px_50px_-20px_rgba(212,175,55,0.25)]"
                    >
                      <div className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-gold-500/0 blur-2xl transition-all duration-500 group-hover:bg-gold-500/10" />
                      <div className="flex items-start justify-between">
                        <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-gold-500/25 bg-navy-800 text-gold-400">
                          <Icon className="h-5 w-5" strokeWidth={1.75} />
                        </span>
                        <span className="font-mono-meta text-xs text-mist/60">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                      </div>
                      <h3 className="font-display mt-6 text-2xl font-semibold leading-snug text-cream">
                        {s.title}
                      </h3>
                      <p className="mt-3 flex-1 text-sm leading-relaxed text-mist">{s.desc}</p>
                      <div className="mt-7 flex flex-wrap items-center justify-between gap-x-5 gap-y-3">
                        <button
                          data-testid={`solution-cta-${cat.slug}-${i}`}
                          onClick={() => onConsult({ title: s.title, area: cat.slug })}
                          className="inline-flex w-fit items-center gap-2 border-b border-gold-500/0 pb-1 text-sm font-bold uppercase tracking-widest text-gold-400 transition-all duration-300 hover:border-gold-500 hover:gap-3"
                        >
                          Consultar sobre este tema
                          <ArrowUpRight className="h-4 w-4" />
                        </button>
                        <Link
                          to={`/areas/${cat.slug}`}
                          data-testid={`solution-more-${cat.slug}-${i}`}
                          className="text-xs font-semibold text-mist underline-offset-4 transition-colors hover:text-gold-400 hover:underline"
                        >
                          Saiba mais
                        </Link>
                      </div>
                    </article>
                  </Reveal>
                );
              })}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
